import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { opportunityService, commentService, documentService, adminService } from '../services/api';
import Meetings from './Meetings';

const STAGE_OPTIONS = [
  'Discovery Session',
  'Qualified',
  'Proposal Validation - Tech & Business',
  'FR Submission',
  'PO Recieved',
  'Delivery Implementation',
  'Customer Sign Off',
  'Cash Claim/Invoice Raised',
  'Launched',
];
const STATUS_OPTIONS = ['submitted', 'in_review', 'approved', 'active', 'closed'];
const DEAL_TYPES = {
  'Professional Services': 'PS',
  'Managed Services': 'MS',
  'Resell': 'Resell',
  'PSO - Resource Augmentation': 'PSO'
};
const PS_SUB_CATEGORIES = ['Infrastructure Services', 'Data & GenAI'];
const RESELL_SUB_CATEGORIES = ['CDN', 'License Resell', 'AWS Resell'];
const DEAL_VALUE_TYPES = ['Customer', 'OEM'];
const REVENUE_SHARE_PRESETS = [5, 10, 15];
const PARTNER_COUNTRIES = [
  'Australia', 'Bangladesh', 'Cambodia', 'China', 'Hong Kong', 'India',
  'Indonesia', 'Japan', 'Malaysia', 'Myanmar', 'Nepal', 'New Zealand',
  'Pakistan', 'Philippines', 'Singapore', 'South Korea', 'Sri Lanka',
  'Taiwan', 'Thailand', 'UAE', 'Vietnam', 'Other'
];
const CURRENT_ENVIRONMENTS = ['On-Premises', 'AWS', 'Azure', 'Google Cloud', 'Other Cloud Provider', 'Hybrid', 'None'];

function getDealTypeLabel(value) {
  const entry = Object.entries(DEAL_TYPES).find(([label, v]) => v === value);
  return entry ? entry[0] : value;
}

function formatUSD(n) {
  if (!n && n !== 0) return '—';
  return '$' + Number(n).toLocaleString('en-US');
}

function Tab({ label, active, onClick }) {
  return (
    <button className={`tab-btn${active ? ' active' : ''}`} onClick={onClick}>{label}</button>
  );
}

export default function OpportunityDetail({ isCKAdmin = false }) {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('details');

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Comments
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState('comment');
  const [postingComment, setPostingComment] = useState(false);
  const [commentError, setCommentError] = useState('');

  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docLabel, setDocLabel] = useState('');
  const fileRef = useRef();

  // Audit
  const [auditLog, setAuditLog] = useState([]);

  useEffect(() => {
    opportunityService.getById(id)
      .then(data => { setOpp(data); setEditForm(data); })
      .catch(() => setError('Opportunity not found'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!opp) return;
    if (tab === 'comments') {
      commentService.getAll(id).then(d => setComments(d.comments || []));
    } else if (tab === 'documents') {
      documentService.list(id).then(d => setDocuments(d.documents || []));
    } else if (tab === 'audit') {
      opportunityService.getAudit(id).then(d => setAuditLog(d.audit || []));
    }
  }, [tab, opp, id]);

  const startEdit = () => {
    setEditForm({ ...opp });
    setEditing(true);
    setSaveMsg('');
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveMsg('');
  };

  const saveEdit = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const service = isCKAdmin ? adminService.updateOpportunity : opportunityService.update;
      await (isCKAdmin
        ? adminService.updateOpportunity(id, editForm)
        : opportunityService.update(id, editForm));
      const updated = await opportunityService.getById(id);
      setOpp(updated);
      setEditing(false);
      setSaveMsg('Saved successfully.');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const setEF = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPostingComment(true);
    setCommentError('');
    try {
      const res = await commentService.add(id, newComment, commentType);
      setComments(prev => [...prev, res.comment]);
      setNewComment('');
    } catch {
      setCommentError('Failed to post comment. Please try again.');
    } finally {
      setPostingComment(false);
    }
  };

  const handleFileUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const { uploadUrl, document: doc } = await documentService.requestUploadUrl(id, file.name, file.type, docLabel);
      await documentService.uploadToS3(uploadUrl, file);
      setDocuments(prev => [...prev, doc]);
      setDocLabel('');
      fileRef.current.value = '';
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="loading">Loading…</div>;
  if (error || !opp) return <div className="message error">{error || 'Not found'}</div>;

  return (
    <div className="opp-detail">
      <div className="opp-detail-header">
        <div>
          <Link to={isCKAdmin ? '/admin' : '/opportunities'} className="back-link">← Back</Link>
          <h2>{opp.opportunity_name}</h2>
          <span className="opp-company">{opp.company_name}</span>
          {opp.cpn_id && <span className="cpn-id-detail">{opp.cpn_id}</span>}
        </div>
        <div className="opp-header-meta">
          <span className={`badge badge-${opp.status}`}>{(opp.status || '').replace('_', ' ')}</span>
          {opp.deal_type && <span className="tag">{getDealTypeLabel(opp.deal_type)}</span>}
          <span className="arr-highlight">{formatUSD(opp.arr_usd)}</span>
          {!editing && (
            <button className="edit-btn" onClick={startEdit}>✏ Edit</button>
          )}
        </div>
      </div>

      {saveMsg && (
        <div className={`message ${saveMsg.startsWith('Saved') ? 'success' : 'error'}`} style={{ marginBottom: '1rem' }}>
          {saveMsg}
        </div>
      )}

      {opp.status === 'pending_partner_approval' && (
        <div className="message info" style={{ marginBottom: '1rem' }}>
          This deal is awaiting manager approval before submission to Cloud Kinetics.
        </div>
      )}

      {opp.rejection_reason && opp.status === 'draft' && (
        <div className="message error" style={{ marginBottom: '1rem' }}>
          <strong>Rejected by {opp.rejected_by}:</strong> {opp.rejection_reason}
        </div>
      )}

      <div className="tab-bar">
        <Tab label="Details" active={tab === 'details'} onClick={() => setTab('details')} />
        <Tab label="Meetings" active={tab === 'meetings'} onClick={() => setTab('meetings')} />
        <Tab label="Comments & Updates" active={tab === 'comments'} onClick={() => setTab('comments')} />
        <Tab label="Documents" active={tab === 'documents'} onClick={() => setTab('documents')} />
        <Tab label="Audit Trail" active={tab === 'audit'} onClick={() => setTab('audit')} />
      </div>

      {tab === 'details' && (
        <div className="tab-content">
          {editing ? (
            <div className="edit-form-wrap">
              <div className="detail-grid">
                <div className="detail-section">
                  <h4 className="section-title">Partner</h4>
                  <div className="form-group">
                    <label>Partner Company</label>
                    <input value={editForm.partner_company || ''} onChange={e => setEF('partner_company', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <select value={editForm.partner_country || ''} onChange={e => setEF('partner_country', e.target.value)}>
                      <option value="">Select…</option>
                      {PARTNER_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div className="detail-section">
                  <h4 className="section-title">Customer</h4>
                  <div className="form-group">
                    <label>Company Name</label>
                    <input value={editForm.company_name || ''} onChange={e => setEF('company_name', e.target.value)} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name</label>
                      <input value={editForm.contact_first_name || ''} onChange={e => setEF('contact_first_name', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Last Name</label>
                      <input value={editForm.contact_last_name || ''} onChange={e => setEF('contact_last_name', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" value={editForm.contact_email || ''} onChange={e => setEF('contact_email', e.target.value)} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input value={editForm.contact_phone || ''} onChange={e => setEF('contact_phone', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Mobile</label>
                      <input value={editForm.contact_mobile || ''} onChange={e => setEF('contact_mobile', e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4 className="section-title">Deal</h4>
                  <div className="form-group">
                    <label>Opportunity Name</label>
                    <input value={editForm.opportunity_name || ''} onChange={e => setEF('opportunity_name', e.target.value)} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>ARR (USD)</label>
                      <input type="number" value={editForm.arr_usd || ''} onChange={e => setEF('arr_usd', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Close Date</label>
                      <input type="date" value={editForm.estimated_close_date || ''} onChange={e => setEF('estimated_close_date', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Deal Stage</label>
                      <select value={editForm.deal_stage || ''} onChange={e => setEF('deal_stage', e.target.value)}>
                        {STAGE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    {isCKAdmin && (
                      <div className="form-group">
                        <label>Status</label>
                        <select value={editForm.status || ''} onChange={e => setEF('status', e.target.value)}>
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                <div className="detail-section">
                  <h4 className="section-title">Deal Classification</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Deal Type</label>
                      <select value={editForm.deal_type || ''} onChange={e => setEF('deal_type', e.target.value)}>
                        <option value="">Select…</option>
                        {Object.entries(DEAL_TYPES).map(([label, value]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Deal Source</label>
                      <select value={editForm.deal_value_type || ''} onChange={e => setEF('deal_value_type', e.target.value)}>
                        <option value="">Select…</option>
                        {DEAL_VALUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  {editForm.deal_type === 'PS' && (
                    <div className="form-group">
                      <label>PS Sub-Category</label>
                      <select value={editForm.ps_sub_category || ''} onChange={e => setEF('ps_sub_category', e.target.value)}>
                        <option value="">Select…</option>
                        {PS_SUB_CATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  {editForm.deal_type === 'Resell' && (
                    <div className="form-group">
                      <label>Resell Sub-Category</label>
                      <select value={editForm.resell_sub_category || ''} onChange={e => setEF('resell_sub_category', e.target.value)}>
                        <option value="">Select…</option>
                        {RESELL_SUB_CATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="form-group" style={{ maxWidth: 320 }}>
                    <label>Revenue Share %</label>
                    <div className="rev-share-picker">
                      {REVENUE_SHARE_PRESETS.map(p => (
                        <button key={p} type="button"
                          className={`rev-share-btn${String(editForm.revenue_share_pct) === String(p) ? ' selected' : ''}`}
                          onClick={() => setEF('revenue_share_pct', p)}
                        >{p}%</button>
                      ))}
                      <button type="button"
                        className={`rev-share-btn${editForm.revenue_share_pct !== '' && !REVENUE_SHARE_PRESETS.map(String).includes(String(editForm.revenue_share_pct)) ? ' selected' : ''}`}
                        onClick={() => setEF('revenue_share_pct', '')}
                      >Custom</button>
                    </div>
                    {editForm.revenue_share_pct !== '' && !REVENUE_SHARE_PRESETS.map(String).includes(String(editForm.revenue_share_pct)) && (
                      <input type="number" style={{ marginTop: '0.5rem' }}
                        value={editForm.revenue_share_pct || ''}
                        onChange={e => setEF('revenue_share_pct', e.target.value)}
                        min="0" max="100" step="0.1" placeholder="Enter %" />
                    )}
                  </div>
                </div>

                <div className="detail-section full-width">
                  <h4 className="section-title">Technical</h4>
                  <div className="form-group">
                    <label>Current Environment</label>
                    <select value={editForm.current_environment || ''} onChange={e => setEF('current_environment', e.target.value)}>
                      <option value="">Select…</option>
                      {CURRENT_ENVIRONMENTS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Business Challenge</label>
                    <textarea rows={4} value={editForm.business_challenge || ''} onChange={e => setEF('business_challenge', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Technical Requirements</label>
                    <textarea rows={4} value={editForm.technical_requirements || ''} onChange={e => setEF('technical_requirements', e.target.value)} />
                  </div>
                  {isCKAdmin && (
                    <div className="form-group">
                      <label>Internal Notes</label>
                      <textarea rows={3} value={editForm.internal_notes || ''} onChange={e => setEF('internal_notes', e.target.value)} placeholder="CK internal notes (not visible to partner)" />
                    </div>
                  )}
                </div>
              </div>

              <div className="edit-actions">
                <button className="btn-primary ck-btn-primary" onClick={saveEdit} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button className="btn-secondary" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="detail-grid">
              <Section title="Partner">
                <Field label="Company" value={opp.partner_company} />
                <Field label="Country" value={opp.partner_country} />
              </Section>

              <Section title="Customer">
                <Field label="Company" value={opp.company_name} />
                <Field label="Contact" value={`${opp.contact_first_name || ''} ${opp.contact_last_name || ''}`.trim()} />
                <Field label="Email" value={opp.contact_email} />
                <Field label="Phone" value={opp.contact_phone} />
                <Field label="Mobile" value={opp.contact_mobile} />
              </Section>

              <Section title="Deal">
                <Field label="Stage" value={opp.deal_stage} />
                <Field label="ARR (USD)" value={formatUSD(opp.arr_usd)} />
                <Field label="Close Date" value={opp.estimated_close_date} />
                <Field label="Deal Type" value={getDealTypeLabel(opp.deal_type)} />
                <Field label="Deal Source" value={opp.deal_value_type} />
                {opp.ps_sub_category && <Field label="PS Sub-Category" value={opp.ps_sub_category} />}
                {opp.resell_sub_category && <Field label="Resell Sub-Category" value={opp.resell_sub_category} />}
              </Section>

              <Section title="Commercial">
                <Field label="Revenue Share %" value={opp.revenue_share_pct != null ? opp.revenue_share_pct + '%' : '—'} />
              </Section>

              <Section title="Technical" fullWidth>
                <Field label="Cloud Services" value={(opp.cloud_services || []).join(', ')} />
                <Field label="Current Environment" value={opp.current_environment} />
                <Field label="Business Challenge" value={opp.business_challenge} multiline />
                {opp.technical_requirements && <Field label="Technical Requirements" value={opp.technical_requirements} multiline />}
              </Section>

              {isCKAdmin && opp.internal_notes && (
                <Section title="Internal Notes" fullWidth>
                  <Field label="Notes" value={opp.internal_notes} multiline />
                </Section>
              )}

              <Section title="Metadata">
                <Field label="CPN ID" value={opp.cpn_id || '—'} />
                <Field label="Status" value={opp.status} />
                <Field label="Submitted" value={new Date(opp.created_at).toLocaleString()} />
                <Field label="Last Updated" value={opp.updated_at ? new Date(opp.updated_at).toLocaleString() : '—'} />
                <Field label="Submitted by" value={opp.created_by || '—'} />
              </Section>
            </div>
          )}
        </div>
      )}

      {tab === 'meetings' && (
        <div className="tab-content">
          <Meetings
            opportunityId={id}
            opportunityName={opp.opportunity_name}
            customerEmail={opp.contact_email}
          />
        </div>
      )}

      {tab === 'comments' && (
        <div className="tab-content">
          <div className="comments-list">
            {comments.length === 0 && <p className="empty-note">No comments yet.</p>}
            {comments.map(c => (
              <div key={c.comment_id} className={`comment-item comment-${c.type}`}>
                <div className="comment-meta">
                  <strong>{c.author_name || c.author_email}</strong>
                  <span className="comment-type-badge">{c.type}</span>
                  <span className="comment-time">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="comment-text">{c.text}</div>
              </div>
            ))}
          </div>

          <div className="comment-compose">
            <div className="comment-compose-header">
              <select value={commentType} onChange={e => setCommentType(e.target.value)} className="comment-type-select">
                <option value="comment">Comment</option>
                <option value="update">Update</option>
                {isCKAdmin && <option value="note">Internal Note</option>}
              </select>
            </div>
            <textarea
              className="comment-input"
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              rows="3"
              placeholder="Add a comment or update…"
            />
            <button className="btn-primary ck-btn-primary" onClick={postComment} disabled={postingComment || !newComment.trim()}>
              {postingComment ? 'Posting…' : 'Post'}
            </button>
            {commentError && <p className="message error" style={{ marginTop: '0.5rem' }}>{commentError}</p>}
          </div>
        </div>
      )}

      {tab === 'documents' && (
        <div className="tab-content">
          <div className="docs-list">
            {documents.length === 0 && <p className="empty-note">No documents uploaded yet.</p>}
            {documents.map(doc => (
              <div key={doc.doc_id} className="doc-item">
                <div className="doc-icon">📄</div>
                <div className="doc-info">
                  <a href={doc.downloadUrl} target="_blank" rel="noreferrer" className="doc-name">{doc.file_name}</a>
                  {doc.label && <span className="tag tag-sub">{doc.label}</span>}
                  <span className="doc-meta">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="doc-upload">
            <h4>Upload Document</h4>
            <div className="upload-row">
              <input
                type="text"
                placeholder="Label (e.g. Proposal, RFP, Contract)"
                value={docLabel}
                onChange={e => setDocLabel(e.target.value)}
                className="doc-label-input"
              />
              <input type="file" ref={fileRef} onChange={handleFileUpload} disabled={uploading} className="file-input" />
              {uploading && <span className="upload-status">Uploading…</span>}
            </div>
          </div>
        </div>
      )}

      {tab === 'audit' && (
        <div className="tab-content">
          {auditLog.length === 0 && <p className="empty-note">No audit records.</p>}
          {auditLog.map(entry => (
            <div key={entry.audit_id} className="audit-entry">
              <div className="audit-meta">
                <span className={`audit-action audit-${entry.action.toLowerCase()}`}>{entry.action}</span>
                <strong>{entry.changed_by}</strong>
                <span className="audit-time">{new Date(entry.changed_at).toLocaleString()}</span>
              </div>
              {entry.diff && entry.diff.length > 0 && (
                <table className="audit-diff">
                  <thead><tr><th>Field</th><th>Before</th><th>After</th></tr></thead>
                  <tbody>
                    {entry.diff.map((d, i) => (
                      <tr key={i}>
                        <td className="diff-field">{d.field}</td>
                        <td className="diff-old">{String(d.old ?? '—')}</td>
                        <td className="diff-new">{String(d.new ?? '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ title, children, fullWidth }) {
  return (
    <div className={`detail-section${fullWidth ? ' full-width' : ''}`}>
      <h4 className="section-title">{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, value, multiline }) {
  return (
    <div className="detail-field">
      <span className="field-label">{label}</span>
      {multiline
        ? <p className="field-value multiline">{value || '—'}</p>
        : <span className="field-value">{value || '—'}</span>
      }
    </div>
  );
}
